/**
 * Client-side Partner Tracker Excel parser.
 *
 * Pure parsing logic — no server or DB dependencies.
 * Mirrors the BFF's partnerTrackerImport.ts parse functions so the
 * file can be parsed in the browser and only the resulting JSON sent to the API.
 */

import * as XLSX from "xlsx";

// ---------------------------------------------------------------------------
// Types (mirrored from BFF)
// ---------------------------------------------------------------------------

interface PartnerTask {
	task_name: string;
	deadline?: string;
	dri?: string;
	progress: "pending" | "in_progress" | "completed";
	comments?: string;
}

interface CustomerSuccess {
	kam?: string;
	tier?: 1 | 2 | 3;
	meeting_frequency?: string;
	tasks_progress?: number;
	missing_items_count?: number;
}

interface MarketingGrant {
	total_grant_usd?: number;
	funds_left?: number;
	grant_expiration_date?: string;
	grant_status?: "Active" | "Expired" | "Pending" | "Completed";
	denomination?: string;
	quarter_year?: string;
	geos?: string[];
	agreement_link?: string;
}

export interface PartnerData {
	partnerName: string;
	customerSuccess: CustomerSuccess;
	marketingGrant: MarketingGrant;
	partnerTasks: PartnerTask[];
	useCase?: string;
}

const INDIVIDUAL_SKIP_SHEETS = new Set([
	"CRM",
	"Marketing Grants Tracking",
	"Onboarding Funnel",
	"Partner Dashboard",
	"Jon's Overview",
	"Carsten's Overview",
	"New",
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalize(name: string): string {
	return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function parseDate(val: unknown): string | undefined {
	if (!val) return undefined;
	if (val instanceof Date) {
		if (val.getFullYear() < 2000) return undefined;
		return val.toISOString().split("T")[0];
	}
	if (typeof val === "number") {
		if (val < 1 || val > 100000) return undefined;
		const d = new Date((val - 25569) * 86400000);
		if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return undefined;
		return d.toISOString().split("T")[0];
	}
	const str = String(val).trim();
	if (!str || str === "0" || str === "00:00:00") return undefined;
	const d = new Date(str);
	if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return undefined;
	return d.toISOString().split("T")[0];
}

function parseNumber(val: unknown): number | undefined {
	if (val == null) return undefined;
	const str = String(val).trim();
	if (!str || str === "N/A" || str === "-") return undefined;
	const n = Number(str);
	return Number.isNaN(n) ? undefined : n;
}

function parseTier(val: unknown): 1 | 2 | 3 | undefined {
	const n = parseNumber(val);
	if (n === 1 || n === 2 || n === 3) return n;
	return undefined;
}

function parseTaskProgress(
	val: unknown,
): "pending" | "in_progress" | "completed" {
	const str = String(val ?? "")
		.trim()
		.toLowerCase();
	if (str === "completed" || str === "complete" || str === "done")
		return "completed";
	if (
		str.includes("progress") ||
		str === "in progress" ||
		str === "in_progress"
	)
		return "in_progress";
	return "pending";
}

function parseMeetingFrequency(val: unknown): string | undefined {
	const str = String(val ?? "").trim();
	if (!str || str === "0") return undefined;
	return str;
}

function parseGrantStatus(
	val: unknown,
): "Active" | "Expired" | "Pending" | "Completed" | undefined {
	const str = String(val ?? "").trim();
	if (str === "Active") return "Active";
	if (str === "Expired") return "Expired";
	if (str === "Pending") return "Pending";
	if (str === "Completed") return "Completed";
	return undefined;
}

function parseGeos(val: unknown): string[] | undefined {
	if (!val) return undefined;
	const str = String(val).trim();
	if (!str) return undefined;
	return str
		.split(/[,;]+/)
		.map((g) => g.trim())
		.filter(Boolean);
}

// ---------------------------------------------------------------------------
// Sheet parsers
// ---------------------------------------------------------------------------

function cellVal(ws: any, row: number, col: number): unknown {
	const addr = XLSX.utils.encode_cell({ r: row, c: col });
	const cell = ws[addr];
	return cell ? cell.v : undefined;
}

function parsePartnerDashboard(
	wb: any,
): Map<string, Omit<PartnerData, "partnerTasks" | "useCase">> {
	const ws = wb.Sheets["Partner Dashboard"];
	if (!ws) return new Map();

	const map = new Map<string, Omit<PartnerData, "partnerTasks" | "useCase">>();
	const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

	for (let r = 7; r <= range.e.r; r++) {
		const name = cellVal(ws, r, 1);
		if (!name || typeof name !== "string") continue;

		const tasksRaw = parseNumber(cellVal(ws, r, 5));
		const tasksProgress =
			tasksRaw != null ? Math.round(tasksRaw * 100) : undefined;

		const totalGrant = parseNumber(cellVal(ws, r, 9));
		const fundsLeft = parseNumber(cellVal(ws, r, 10));
		const grantExpDate = parseDate(cellVal(ws, r, 8));
		const grantStatus = parseGrantStatus(cellVal(ws, r, 11));

		const cs: CustomerSuccess = {};
		const kam = cellVal(ws, r, 2);
		if (kam && String(kam).trim() && String(kam).trim() !== "0")
			cs.kam = String(kam).trim();
		const tier = parseTier(cellVal(ws, r, 3));
		if (tier) cs.tier = tier;
		const meetFreq = parseMeetingFrequency(cellVal(ws, r, 7));
		if (meetFreq) cs.meeting_frequency = meetFreq;
		if (tasksProgress != null) cs.tasks_progress = tasksProgress;
		const missing = parseNumber(cellVal(ws, r, 6));
		if (missing != null) cs.missing_items_count = missing;

		const mg: MarketingGrant = {};
		if (totalGrant != null && totalGrant > 0) mg.total_grant_usd = totalGrant;
		if (fundsLeft != null) mg.funds_left = fundsLeft;
		if (grantExpDate) mg.grant_expiration_date = grantExpDate;
		if (grantStatus) mg.grant_status = grantStatus;

		map.set(normalize(name), {
			partnerName: name.trim(),
			customerSuccess: cs,
			marketingGrant: mg,
		});
	}

	return map;
}

function parseMarketingGrantsTracking(
	wb: any,
): Map<string, Partial<MarketingGrant>> {
	const ws = wb.Sheets["Marketing Grants Tracking"];
	if (!ws) return new Map();

	const map = new Map<string, Partial<MarketingGrant>>();
	const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

	for (let r = 10; r <= range.e.r; r++) {
		const name = cellVal(ws, r, 1);
		if (!name || typeof name !== "string") continue;

		const extra: Partial<MarketingGrant> = {};
		const denom = cellVal(ws, r, 3);
		if (denom && String(denom).trim())
			extra.denomination = String(denom).trim();
		const qy = cellVal(ws, r, 2);
		if (qy && String(qy).trim()) {
			const d = parseDate(qy);
			if (d) extra.quarter_year = d;
			else extra.quarter_year = String(qy).trim();
		}
		const geos = parseGeos(cellVal(ws, r, 7));
		if (geos?.length) extra.geos = geos;
		const status = parseGrantStatus(cellVal(ws, r, 8));
		if (status) extra.grant_status = status;
		const link = cellVal(ws, r, 9);
		if (link && String(link).trim()) extra.agreement_link = String(link).trim();

		map.set(normalize(name), extra);
	}

	return map;
}

function parseJonsOverview(
	wb: any /* SheetJS workbook */,
): Map<string, string> {
	const ws = wb.Sheets["Jon's Overview"];
	if (!ws) return new Map();

	const map = new Map<string, string>();
	const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

	for (let r = 2; r <= range.e.r; r++) {
		const name = cellVal(ws, r, 0);
		if (!name || typeof name !== "string") continue;
		const useCase = cellVal(ws, r, 3);
		if (useCase && String(useCase).trim()) {
			map.set(normalize(name), String(useCase).trim());
		}
	}

	return map;
}

function parseIndividualPartnerSheet(
	ws: any,
): { partnerName: string; tasks: PartnerTask[] } | null {
	const name = cellVal(ws, 1, 2);
	if (!name || typeof name !== "string") return null;

	const tasks: PartnerTask[] = [];
	const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

	for (let r = 11; r <= range.e.r; r++) {
		const taskName = cellVal(ws, r, 1);
		if (!taskName || typeof taskName !== "string") continue;
		if (taskName.trim().toLowerCase() === "task") continue;

		const task: PartnerTask = {
			task_name: taskName.trim(),
			progress: parseTaskProgress(cellVal(ws, r, 4)),
		};

		const deadline = cellVal(ws, r, 2);
		if (deadline) {
			const d = parseDate(deadline);
			if (d) task.deadline = d;
			else {
				const s = String(deadline).trim();
				if (s && s !== "0") task.deadline = s;
			}
		}

		const dri = cellVal(ws, r, 3);
		if (dri && String(dri).trim()) task.dri = String(dri).trim();

		const comments = cellVal(ws, r, 6);
		if (comments && String(comments).trim())
			task.comments = String(comments).trim();

		tasks.push(task);
	}

	return { partnerName: name.trim(), tasks };
}

function parseAllIndividualSheets(
	wb: any /* SheetJS workbook */,
): Map<string, PartnerTask[]> {
	const map = new Map<string, PartnerTask[]>();

	for (const sheetName of wb.SheetNames) {
		if (INDIVIDUAL_SKIP_SHEETS.has(sheetName)) continue;
		const ws = wb.Sheets[sheetName];
		const result = parseIndividualPartnerSheet(ws);
		if (result && result.tasks.length > 0) {
			map.set(normalize(result.partnerName), result.tasks);
		}
	}

	return map;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse an Excel ArrayBuffer into an array of partner data.
 * Runs entirely in the browser — no server round-trip needed.
 */
export function parsePartnerTrackerFile(buffer: ArrayBuffer): PartnerData[] {
	const wb = XLSX.read(buffer, { type: "array", cellDates: true });

	const dashboardData = parsePartnerDashboard(wb);
	const grantsExtra = parseMarketingGrantsTracking(wb);
	const useCaseMap = parseJonsOverview(wb);
	const tasksMap = parseAllIndividualSheets(wb);

	const allPartners = new Map<string, PartnerData>();

	for (const [key, val] of dashboardData) {
		allPartners.set(key, {
			partnerName: val.partnerName,
			customerSuccess: val.customerSuccess,
			marketingGrant: { ...val.marketingGrant },
			partnerTasks: tasksMap.get(key) ?? [],
			useCase: useCaseMap.get(key),
		});
	}

	for (const [key, extra] of grantsExtra) {
		const existing = allPartners.get(key);
		if (existing) {
			Object.assign(existing.marketingGrant, extra);
		}
	}

	for (const [key, tasks] of tasksMap) {
		if (!allPartners.has(key)) {
			allPartners.set(key, {
				partnerName: key,
				customerSuccess: {},
				marketingGrant: {},
				partnerTasks: tasks,
				useCase: useCaseMap.get(key),
			});
		}
	}

	return Array.from(allPartners.values());
}
